/* eslint-disable consistent-return */
/**
 * Tasks V2 Migration — In-Migration Checklist Config slice.
 *
 * Solution B of the cockpit-coverage plan (PLAN.md §6). Standalone
 * slice — does NOT touch ``tasksV2Migration.js`` because two parallel
 * agents are working on Solutions A and C in that file at the same
 * time as this slice was authored.
 *
 * Endpoints called map 1:1 to BE routes in
 * ``apps/management/tasks_migration_template_clone_views.py``:
 *   GET   /practices/<id>/templates/                                — fetchPracticeMigrationTemplates
 *   POST  /practices/<id>/templates/clone-default/                  — cloneDefaultTemplate
 *   POST  /practices/<id>/templates/<tid>/test-on-sample/           — testTemplateOnSample
 *   POST  /practices/<id>/templates/<tid>/archive/                  — archivePracticeMigrationTemplate
 *
 * 409 lock errors are surfaced via a structured object so the panel
 * can render the same locked-state banner used by
 * TemplateSelectionPanel without overwriting the templates list.
 *
 * TODO(integrator): register this slice in ``src/redux/store.js``:
 *
 *     import tasksV2ChecklistConfigReducer from './slices/tasksV2ChecklistConfig';
 *
 *     const rootReducer = combineReducers({
 *         ...
 *         tasksV2ChecklistConfig: tasksV2ChecklistConfigReducer,
 *         ...
 *     });
 *
 * Once registered, ``useSelector(s => s.tasksV2ChecklistConfig)`` will
 * return the shape defined in ``initialState`` below.
 */
import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { callAdminApi } from '../../utils/apiCall';

const BASE = '/api/v1/management/tasks-migration';


const initialState = {
    // List of practice-scope migration templates (compact rows).
    templates: [],
    // Lock state surfaced by the BE so the panel can disable actions
    // immediately without a second round-trip.
    locked: false,
    lockedAt: null,
    // BE feature flag — turns false when GET /templates/ returns 404
    // (TASKS_V2_MIGRATION_PREVIEW_V2 OFF).
    featureAvailable: true,

    // Loading / saving flags. Per-action so multiple buttons can be
    // in flight without conflict.
    loadingList: false,
    cloning: false,
    archivingId: null,
    testingOnSample: false,

    // Last action errors. Cloning / archiving may surface a structured
    // ``{code, message}`` so the panel can render the lock banner
    // without losing the templates list.
    lastError: null,

    // The most recent test-on-sample projection (rendered by
    // TestOnSampleDialog in TaskCardPreview). Cleared when the dialog
    // closes.
    lastSamplePreview: null,
};


const slice = createSlice({
    name: 'tasksV2ChecklistConfig',
    initialState,
    reducers: {
        setLoadingList(state, action) {
            state.loadingList = !!action.payload;
        },
        setListSnapshot(state, action) {
            const { templates, locked, lockedAt } = action.payload;
            state.templates = templates || [];
            state.locked = !!locked;
            state.lockedAt = lockedAt || null;
            state.featureAvailable = true;
        },
        setFeatureUnavailable(state) {
            state.featureAvailable = false;
            state.templates = [];
            state.locked = false;
            state.lockedAt = null;
        },
        setLastError(state, action) {
            state.lastError = action.payload;
        },
        clearLastError(state) {
            state.lastError = null;
        },
        setCloning(state, action) {
            state.cloning = !!action.payload;
        },
        setArchivingId(state, action) {
            state.archivingId = action.payload;
        },
        setTestingOnSample(state, action) {
            state.testingOnSample = !!action.payload;
        },
        setSamplePreview(state, action) {
            state.lastSamplePreview = action.payload;
        },
        clearSamplePreview(state) {
            state.lastSamplePreview = null;
        },
        // Optimistic insert / update / archive so the panel feels snappy.
        upsertTemplate(state, action) {
            const tpl = action.payload;
            if (!tpl?.id) return;
            const idx = state.templates.findIndex((t) => t.id === tpl.id);
            if (idx >= 0) {
                state.templates[idx] = tpl;
            } else {
                state.templates.unshift(tpl);
            }
        },
    },
});


export default slice.reducer;
export const {
    clearLastError,
    clearSamplePreview,
} = slice.actions;


// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------


/**
 * GET /practices/<id>/templates/
 *
 * Fetches the list of practice-scope migration templates (clones the
 * operator has authored). Stores the rows + lock state in the slice.
 */
export function fetchPracticeMigrationTemplates(practiceId) {
    return async () => {
        if (!practiceId) return null;
        dispatch(slice.actions.setLoadingList(true));
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/templates/`,
            );
            if (response.status === 404) {
                dispatch(slice.actions.setFeatureUnavailable());
                return null;
            }
            if (response.status !== 200) {
                dispatch(slice.actions.setLastError({
                    code: response.data?.code || 'LIST_FAILED',
                    message: response.data?.detail
                        || `Failed to load practice templates (HTTP ${response.status})`,
                }));
                return null;
            }
            dispatch(slice.actions.setListSnapshot({
                templates: response.data?.templates ?? [],
                locked: !!response.data?.locked,
                lockedAt: response.data?.locked_at ?? null,
            }));
            return response.data;
        } catch (error) {
            dispatch(slice.actions.setLastError({
                code: 'NETWORK_ERROR',
                message: error?.message || String(error),
            }));
            return null;
        } finally {
            dispatch(slice.actions.setLoadingList(false));
        }
    };
}


/**
 * POST /practices/<id>/templates/clone-default/
 *
 * Body: ``{source_template_id, target_treatment_item_ids}``.
 *
 * On success: returns the cloned template + refetches the list so the
 * panel renders the new row. On 409 LOCKED: stores the structured
 * error so the panel can show a "locked" banner without overwriting
 * the existing list.
 */
export function cloneDefaultTemplate(practiceId, payload) {
    return async () => {
        if (!practiceId) return null;
        dispatch(slice.actions.setCloning(true));
        dispatch(slice.actions.clearLastError());
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/templates/clone-default/`,
                'POST',
                payload,
            );
            if (response.status === 201) {
                dispatch(slice.actions.upsertTemplate(response.data));
                // Refetch to make sure lock state is fresh.
                dispatch(fetchPracticeMigrationTemplates(practiceId));
                return response.data;
            }
            if (response.status === 409) {
                dispatch(slice.actions.setLastError({
                    code: response.data?.code || 'TEMPLATE_SELECTION_LOCKED',
                    message: response.data?.detail
                        || 'Template selection is locked for the active migration run.',
                }));
                // Refresh the list so the panel updates the locked banner.
                dispatch(fetchPracticeMigrationTemplates(practiceId));
                return null;
            }
            if (response.status === 404) {
                dispatch(slice.actions.setFeatureUnavailable());
                return null;
            }
            dispatch(slice.actions.setLastError({
                code: response.data?.code || 'CLONE_FAILED',
                message: response.data?.detail
                    || `Failed to clone template (HTTP ${response.status})`,
            }));
            return null;
        } catch (error) {
            dispatch(slice.actions.setLastError({
                code: 'NETWORK_ERROR',
                message: error?.message || String(error),
            }));
            return null;
        } finally {
            dispatch(slice.actions.setCloning(false));
        }
    };
}


/**
 * POST /practices/<id>/templates/<template_id>/test-on-sample/
 *
 * Body: ``{reminder_id}``.
 *
 * Returns a projection in the same shape as ``DryRunPreviewRow`` so
 * the panel can render it with TaskCardPreview.
 */
export function testTemplateOnSample(practiceId, templateId, reminderId) {
    return async () => {
        if (!practiceId || !templateId || !reminderId) return null;
        dispatch(slice.actions.setTestingOnSample(true));
        dispatch(slice.actions.clearLastError());
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/templates/${templateId}/test-on-sample/`,
                'POST',
                { reminder_id: reminderId },
            );
            if (response.status === 200) {
                dispatch(slice.actions.setSamplePreview(response.data));
                return response.data;
            }
            if (response.status === 404) {
                dispatch(slice.actions.setLastError({
                    code: response.data?.code || 'TEST_NOT_FOUND',
                    message: response.data?.detail || 'Reminder or template not found.',
                }));
                return null;
            }
            dispatch(slice.actions.setLastError({
                code: response.data?.code || 'TEST_FAILED',
                message: response.data?.detail
                    || `Failed to test on sample (HTTP ${response.status})`,
            }));
            return null;
        } catch (error) {
            dispatch(slice.actions.setLastError({
                code: 'NETWORK_ERROR',
                message: error?.message || String(error),
            }));
            return null;
        } finally {
            dispatch(slice.actions.setTestingOnSample(false));
        }
    };
}


/**
 * POST /practices/<id>/templates/<template_id>/archive/
 *
 * Soft-archives a practice-scope migration template. Lock-aware (409).
 */
export function archivePracticeMigrationTemplate(practiceId, templateId) {
    return async () => {
        if (!practiceId || !templateId) return null;
        dispatch(slice.actions.setArchivingId(templateId));
        dispatch(slice.actions.clearLastError());
        try {
            const response = await callAdminApi(
                `${BASE}/practices/${practiceId}/templates/${templateId}/archive/`,
                'POST',
                {},
            );
            if (response.status === 200) {
                dispatch(slice.actions.upsertTemplate(response.data));
                return response.data;
            }
            if (response.status === 409) {
                dispatch(slice.actions.setLastError({
                    code: response.data?.code || 'TEMPLATE_SELECTION_LOCKED',
                    message: response.data?.detail
                        || 'Template selection is locked for the active migration run.',
                }));
                dispatch(fetchPracticeMigrationTemplates(practiceId));
                return null;
            }
            if (response.status === 403) {
                dispatch(slice.actions.setLastError({
                    code: response.data?.code || 'ARCHIVE_FORBIDDEN',
                    message: response.data?.detail
                        || 'You are not allowed to archive this template.',
                }));
                return null;
            }
            dispatch(slice.actions.setLastError({
                code: response.data?.code || 'ARCHIVE_FAILED',
                message: response.data?.detail
                    || `Failed to archive template (HTTP ${response.status})`,
            }));
            return null;
        } catch (error) {
            dispatch(slice.actions.setLastError({
                code: 'NETWORK_ERROR',
                message: error?.message || String(error),
            }));
            return null;
        } finally {
            dispatch(slice.actions.setArchivingId(null));
        }
    };
}
